import { z } from 'zod';

// =============================================================================
// INVITE PREVIEW — GET /api/invites/preview
// Public (unauthenticated) endpoint. Returns enough display info for the signup
// page to render personalized copy. Does NOT echo the invitee's email back —
// the user just opened their email at that address, no need to reflect it,
// and not echoing reduces leakage if the URL ends up in browser history,
// referrer logs, or client-side telemetry.
// =============================================================================

export const invitePreviewQuery = z.object({
  token: z.string().min(1).max(2048),
});

/** An invitation to an org or hub role. */
const inviteRolePreviewResponse = z.object({
  inviter_name: z.string(),
  entity_id: z.string().uuid(),
  entity_name: z.string(),
  entity_type: z.enum(['org', 'hub']),
  role: z.enum(['org_admin', 'admin', 'team_user', 'user']),
  expires_at: z.string().datetime(),
});

/**
 * An invitation from a person to connect (gh #4661). `entity_id` is the invite's own opaque
 * id, never the inviter's user id. `inviter_name` is the inviter's display name as it was when
 * they invited, or null when they had none or it read like a link; clients then render a
 * localized "A WayAI user".
 */
const inviteContactPreviewResponse = z.object({
  inviter_name: z.string().nullable(),
  entity_id: z.string().uuid(),
  entity_type: z.literal('contact'),
  role: z.literal('contact'),
  expires_at: z.string().datetime(),
});

export const invitePreviewResponse = z.union([inviteRolePreviewResponse, inviteContactPreviewResponse]);

export type InvitePreviewResponse = z.infer<typeof invitePreviewResponse>;

// =============================================================================
// "DON'T EMAIL ME AGAIN" — POST /api/invites/unsubscribe
// Public (unauthenticated): the token from a contact invitation email's link. That link opens
// a web page that writes nothing; only this POST adds the address to the suppression list.
// =============================================================================

export const inviteUnsubscribeBody = z.object({ token: z.string().min(1).max(256) }).strict();

export type InviteUnsubscribeBody = z.infer<typeof inviteUnsubscribeBody>;
